/**
 * UUIDv7 — identificadores gerados no CLIENTE.
 *
 * Decisão da Architecture Review §7.3. Sem ela, mutações dependentes quebram:
 * criar uma atividade offline e editá-la em seguida geraria uma segunda
 * mutação apontando para um id que o servidor ainda não conhece, exigindo
 * remapeamento na sincronização — a fonte clássica de bug em outbox.
 *
 * v7 e não v4 porque o prefixo é o timestamp: os ids ficam ordenáveis no
 * tempo e o índice do Postgres não fragmenta a cada inserção.
 *
 * Layout (RFC 9562):
 *   48 bits  timestamp em ms
 *    4 bits  versão (7)
 *   12 bits  contador/aleatório  ← usado aqui para monotonicidade
 *    2 bits  variante (0b10)
 *   62 bits  aleatório
 */

let ultimoInstante = -1;
let ultimoContador = 0;

function bytesAleatorios(tamanho: number): Uint8Array {
  const buffer = new Uint8Array(tamanho);
  globalThis.crypto.getRandomValues(buffer);
  return buffer;
}

/**
 * Gera um UUIDv7.
 *
 * Monotônico dentro do mesmo milissegundo: um contador de 12 bits incrementa
 * a cada chamada. Sem isso, duas mutações criadas no mesmo tique poderiam
 * sair da ordem, e a ordem do outbox é obrigatória.
 */
export function uuidv7(now: number = Date.now()): string {
  if (now === ultimoInstante) {
    ultimoContador += 1;
    // Estouro do contador: espera o próximo milissegundo em vez de repetir.
    if (ultimoContador > 0xfff) {
      return uuidv7(now + 1);
    }
  } else {
    ultimoInstante = now;
    // Começa baixo e aleatório, para não vazar volume de escrita.
    ultimoContador = bytesAleatorios(2)[0]! & 0x0ff;
  }

  const bytes = new Uint8Array(16);

  // 48 bits de timestamp, big-endian.
  bytes[0] = (now / 2 ** 40) & 0xff;
  bytes[1] = (now / 2 ** 32) & 0xff;
  bytes[2] = (now / 2 ** 24) & 0xff;
  bytes[3] = (now / 2 ** 16) & 0xff;
  bytes[4] = (now / 2 ** 8) & 0xff;
  bytes[5] = now & 0xff;

  // Versão 7 nos 4 bits altos, contador nos 12 seguintes.
  bytes[6] = 0x70 | ((ultimoContador >> 8) & 0x0f);
  bytes[7] = ultimoContador & 0xff;

  const resto = bytesAleatorios(8);
  // Variante RFC 4122 nos 2 bits altos.
  bytes[8] = (resto[0]! & 0x3f) | 0x80;
  for (let i = 1; i < 8; i += 1) bytes[8 + i] = resto[i]!;

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const PADRAO_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function isUuid(value: string): boolean {
  return PADRAO_UUID.test(value);
}

export function uuidVersion(value: string): number | null {
  if (!isUuid(value)) return null;
  return Number.parseInt(value[14] as string, 16);
}

/** Instante embutido no prefixo de um UUIDv7. */
export function uuidv7Timestamp(value: string): number | null {
  if (uuidVersion(value) !== 7) return null;
  return Number.parseInt(value.replace(/-/g, '').slice(0, 12), 16);
}

/** Apenas para teste: zera o estado de monotonicidade. */
export function resetUuidv7State(): void {
  ultimoInstante = -1;
  ultimoContador = 0;
}
